import React, { useEffect, useRef, useState } from 'react';
import { getFileRefIcon, getSnippetIcon } from '../../../shared/utils/getFileIconAndExtensionHelper';
import './transfer-notifications.scss';

export type TransferDirection = 'upload' | 'download';
export type TransferItemType = 'file' | 'folder' | 'snippet';

type TransferNoticePayload = {
  id?: number;
  direction: TransferDirection;
  itemType: TransferItemType;
  name: string;
};

type TransferNotice = TransferNoticePayload & {
  id: number;
  status: 'active' | 'complete';
};

const TRANSFER_NOTICE_EVENT = 'flashroom-transfer-notice';
const TRANSFER_NOTICE_COMPLETE_EVENT = 'flashroom-transfer-notice-complete';
const TRANSFER_NOTICE_DURATION = 10000;
const completedNoticeIds = new Set<number>();

function getExtensionFromName(name: string) {
  const dotIndex = name.lastIndexOf('.');
  if (dotIndex === -1) return 'text';

  return name.slice(dotIndex + 1).toLowerCase();
}

function getSnippetIconHint(name: string) {
  const ext = getExtensionFromName(name);
  const iconHints: Record<string, string> = {
    cs: 'csharp',
    ino: 'arduino',
    js: 'javascript',
    kt: 'kotlin',
    py: 'python',
    rb: 'ruby',
    rs: 'rust',
    ts: 'typescript',
    txt: 'text',
  };

  return iconHints[ext] || ext;
}

function imageFileName(iconName: string) {
  return iconName.includes('.') ? iconName : `${iconName}.png`;
}

function getTransferIcon(notice: TransferNotice) {
  if (notice.itemType === 'folder') {
    return `${process.env.PUBLIC_URL}/images/folder-closed-dark.png`;
  }

  if (notice.itemType === 'snippet') {
    return `${process.env.PUBLIC_URL}/images/${imageFileName(getSnippetIcon(getSnippetIconHint(notice.name)))}`;
  }

  return `${process.env.PUBLIC_URL}/images/${imageFileName(getFileRefIcon(notice.name))}`;
}

export function showTransferNotice(payload: TransferNoticePayload) {
  const id = payload.id || Date.now() + Math.random();

  window.dispatchEvent(
    new CustomEvent<TransferNoticePayload>(TRANSFER_NOTICE_EVENT, {
      detail: {
        ...payload,
        id,
      },
    })
  );

  return id;
}

export function completeTransferNotice(id: number) {
  window.dispatchEvent(
    new CustomEvent<number>(TRANSFER_NOTICE_COMPLETE_EVENT, {
      detail: id,
    })
  );
}

const TransferNotifications = () => {
  const [notices, setNotices] = useState<TransferNotice[]>([]);
  const removalTimersRef = useRef<Map<number, number>>(new Map());

  useEffect(() => {
    const removalTimers = removalTimersRef.current;

    const clearRemovalTimer = (id: number) => {
      const timer = removalTimers.get(id);
      if (timer) {
        window.clearTimeout(timer);
        removalTimers.delete(id);
      }
    };

    const scheduleRemoval = (id: number) => {
      clearRemovalTimer(id);
      const timer = window.setTimeout(() => {
        completedNoticeIds.delete(id);
        removalTimers.delete(id);
        setNotices((current) => current.filter((notice) => notice.id !== id));
      }, TRANSFER_NOTICE_DURATION);

      removalTimers.set(id, timer);
    };

    const onNotice = (event: Event) => {
      const detail = (event as CustomEvent<TransferNoticePayload>).detail;
      if (!detail?.name) return;

      const id = detail.id || Date.now() + Math.random();
      setNotices((current) => [
        ...current,
        {
          id,
          direction: detail.direction,
          itemType: detail.itemType,
          name: detail.name,
          status: completedNoticeIds.has(id) ? 'complete' : 'active',
        },
      ]);

      if (completedNoticeIds.has(id)) {
        scheduleRemoval(id);
      }
    };

    window.addEventListener(TRANSFER_NOTICE_EVENT, onNotice);

    const onComplete = (event: Event) => {
      const id = (event as CustomEvent<number>).detail;
      if (!id) return;
      completedNoticeIds.add(id);

      setNotices((current) =>
        current.map((notice) =>
          notice.id === id
            ? {
                ...notice,
                status: 'complete',
              }
            : notice
        )
      );

      scheduleRemoval(id);

    };

    window.addEventListener(TRANSFER_NOTICE_COMPLETE_EVENT, onComplete);

    return () => {
      removalTimers.forEach((timer) => window.clearTimeout(timer));
      removalTimers.clear();
      window.removeEventListener(TRANSFER_NOTICE_EVENT, onNotice);
      window.removeEventListener(TRANSFER_NOTICE_COMPLETE_EVENT, onComplete);
    };
  }, []);

  const closeNotice = (id: number) => {
    const timer = removalTimersRef.current.get(id);
    if (timer) {
      window.clearTimeout(timer);
      removalTimersRef.current.delete(id);
    }
    completedNoticeIds.delete(id);
    setNotices((current) => current.filter((notice) => notice.id !== id));
  };

  if (!notices.length) return null;

  return (
    <div className="transfer-notice-stack" aria-live="polite">
      {notices.map((notice) => {
        const icon = getTransferIcon(notice);

        return (
          <div className={`transfer-notice ${notice.status}`} role="status" key={notice.id}>
            <img
              className="transfer-notice-icon"
              src={icon}
              alt=""
              aria-hidden="true"
            />

            <div className="transfer-notice-copy">
              <span className="transfer-notice-action">
                {notice.status === 'complete'
                  ? notice.direction === 'upload'
                    ? 'Uploaded'
                    : 'Downloaded'
                  : notice.direction === 'upload'
                    ? 'Uploading'
                    : 'Downloading'}
              </span>
              <span className="transfer-notice-name">{notice.name}</span>
              {notice.status === 'active' && (
                <span className={`transfer-notice-motion ${notice.direction}`}>
                  <span />
                  <span />
                  <span />
                </span>
              )}
              {notice.status === 'complete' && (
                <span className="transfer-notice-complete-text">
                  {notice.direction === 'upload' ? 'Upload completed' : 'Download completed'}
                </span>
              )}
            </div>

            <button
              type="button"
              className="transfer-notice-close"
              aria-label="Close notification"
              onClick={() => closeNotice(notice.id)}
            >
              &times;
            </button>
          </div>
        );
      })}
    </div>
  );
};

export default TransferNotifications;
